import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
// Highcharts renders to SVG/canvas, so it needs a real colour — CSS variables
// are not resolved inside chart option objects.
import { PRIMARY_BG } from '../../../../theme/brand';
import {
  Box,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Button
} from '@chakra-ui/react';
import { getUserTimeZone } from '../../../../utils/DefaultFunction';
import moment from 'moment';

const PerformanceChart = ({ performanceData, loading, error, onRetry }) => {

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">Loading performance data...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={onRetry} colorScheme="blue">
          Retry
        </Button>
      </Box>
    );
  }

  if (!performanceData) {
    return (
      <Box p={6} textAlign="center">
        <Text color="gray.500">No performance data available</Text>
      </Box>
    );
  }

  const { hourly_performance_metrics } = performanceData;

  // Extract data for the chart
  const timeLabels = hourly_performance_metrics?.map(item => moment.utc(item.datetime_utc).tz(getUserTimeZone()).format("hh:mm a")) || [];
  const sentData = hourly_performance_metrics?.map(item => item.sent) || [];
  const deliveredData = hourly_performance_metrics?.map(item => item.delivered) || [];
  const openedData = hourly_performance_metrics?.map(item => item.read) || [];
  const failedData = hourly_performance_metrics?.map(item => item.failed) || [];

  // Calculate max value for Y-axis
  const maxValue = Math.max(...sentData, ...deliveredData, ...openedData, ...failedData) * 1.1;

  const chartOptions = {
    chart: {
      type: 'column',
      height: 500,
      backgroundColor: 'transparent',
      style: {
        fontFamily: 'Inter, system-ui, sans-serif'
      }
    },
    title: {
      text: 'Hourly Performance Metrics',
      align: 'left',
      style: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#1a202c'
      }
    },
    subtitle: {
      text: 'Campaign performance breakdown by hour',
      align: 'left',
      style: {
        fontSize: '12px',
        color: '#718096'
      }
    },
    xAxis: {
      categories: timeLabels.length > 0 ? timeLabels : ['No Data'],
      crosshair: true,
      lineColor: '#e2e8f0',
      tickColor: '#e2e8f0',
      gridLineColor: '#0000001a',
      gridLineDashStyle: 'Dash',
      labels: {
        style: {
          color: '#718096',
          fontSize: '12px'
        }
      }
    },
    yAxis: {
      min: 0,
      max: maxValue > 0 ? maxValue : 100,
      title: {
        text: 'Messages',
        style: {
          color: '#718096',
          fontSize: '12px'
        }
      },
      gridLineColor: '#0000001a',
      gridLineDashStyle: 'Dash',
      labels: {
        style: {
          color: '#718096',
          fontSize: '12px'
        }
      }
    },
    tooltip: {
      headerFormat: '<span style="font-size:12px"><b>{point.x}</b></span><table>',
      pointFormat: '<tr><td style="color:{series.color};padding:0">{series.name}: </td>' +
        '<td style="padding:0"><b>{point.y:,.0f}</b></td></tr>',
      footerFormat: '</table>',
      shared: true,
      useHTML: true,
      backgroundColor: 'white',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      shadow: true
    },
    plotOptions: {
      column: {
        pointPadding: 0.1,
        borderWidth: 0,
        groupPadding: 0.1,
        borderRadius: 3
      }
    },
    legend: {
      align: 'right',
      verticalAlign: 'top',
      floating: true,
      backgroundColor: 'white',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      borderRadius: 8,
      shadow: false,
      itemStyle: {
        fontSize: '12px',
        color: '#4a5568'
      },
      symbolHeight: 12,
      symbolWidth: 12,
      symbolRadius: 2
    },
    credits: {
      enabled: false
    },
    series: [
      {
        name: 'Sent',
        color: PRIMARY_BG,
        data: sentData.length > 0 ? sentData : [0]
      },
      {
        name: 'Delivered',
        color: '#38a169',
        data: deliveredData.length > 0 ? deliveredData : [0]
      },
      {
        name: 'Read',
        color: '#8b5cf6',
        data: openedData.length > 0 ? openedData : [0]
      },
      {
        name: 'Failed',
        color: '#e53e3e',
        data: failedData.length > 0 ? failedData : [0]
      }
    ],
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          legend: {
            floating: false,
            align: 'center',
            verticalAlign: 'bottom'
          }
        }
      }]
    }
  };

  return (
    <Box >


      {/* Main Performance Chart */}
      <Box className='box-style-card' p={4} shadow="sm">
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
        />
      </Box>


    </Box>
  );
};

export default PerformanceChart;